import { useCallback, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'

export function Kbd({ children }: { children: string }) {
  return (
    <kbd className="inline-flex h-[18px] min-w-[18px] items-center justify-center rounded border border-zinc-300 bg-zinc-100 px-1 text-[10px] font-medium leading-none text-zinc-500">
      {children}
    </kbd>
  )
}

const EDGE_PADDING = 8

export function ActionTooltip({
  label,
  shortcut,
  children,
  position = 'top',
}: {
  label: string
  shortcut?: ReactNode
  children: ReactNode
  position?: 'top' | 'bottom'
}) {
  const triggerRef = useRef<HTMLDivElement>(null)
  const [visible, setVisible] = useState(false)
  const delayRef = useRef<ReturnType<typeof setTimeout>>(undefined)

  // Ref callback: fires once the tooltip DOM node is mounted and measurable.
  // Positions it relative to the trigger, clamped to viewport edges.
  const tooltipRefCallback = useCallback((node: HTMLDivElement | null) => {
    if (!node || !triggerRef.current) return

    const tr = triggerRef.current.getBoundingClientRect()
    const tt = node.getBoundingClientRect()
    const vw = window.innerWidth

    // Vertical: prefer the requested position, flip if it doesn't fit
    const wantsTop = position === 'top'
    const fitsTop = tr.top - 6 - tt.height >= 0
    const above = wantsTop && fitsTop

    const top = above ? tr.top - 6 - tt.height : tr.bottom + 6

    // Horizontal: center on trigger, then clamp to viewport
    let left = tr.left + tr.width / 2 - tt.width / 2
    if (left < EDGE_PADDING) left = EDGE_PADDING
    if (left + tt.width > vw - EDGE_PADDING) left = vw - EDGE_PADDING - tt.width

    node.style.top = `${top}px`
    node.style.left = `${left}px`
    node.style.opacity = '1'
  }, [position])

  const show = useCallback(() => {
    delayRef.current = setTimeout(() => setVisible(true), 200)
  }, [])

  const hide = useCallback(() => {
    clearTimeout(delayRef.current)
    setVisible(false)
  }, [])

  return (
    <div ref={triggerRef} onMouseEnter={show} onMouseLeave={hide}>
      {children}
      {visible && createPortal(
        <div
          ref={tooltipRefCallback}
          style={{ position: 'fixed', zIndex: 9999, opacity: 0 }}
          className="pointer-events-none flex items-center gap-1.5 rounded-md bg-zinc-900 px-2.5 py-1.5 text-[11px] whitespace-nowrap text-white shadow-lg"
        >
          <span>{label}</span>
          {shortcut}
        </div>,
        document.body
      )}
    </div>
  )
}
